import { useState, useEffect, } from 'react';
import { useParams, useNavigate, Link, } from 'react-router-dom';
import _ from 'lodash';
import { useFirstMountState, } from 'react-use';
import { useSnackbar, } from 'notistack';
import {
    Box,
    Typography,
    Stack,
    IconButton,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    MenuItem, } from '@mui/material';
import AbcIcon from '@mui/icons-material/Abc';
import { writeBatch, doc, } from 'firebase/firestore';
import { db, } from '../../firebase/config';
import { useAuth, useCollection, useDocument, } from '../../hooks';
import SimpleBackdrop from '../../components/simple-backdrop';
import OperationsCustomizedMenu from '../../components/operations-customized-menu';
import ListFoldersCustomChips from './ListFoldersCustomChips';
import Mode from './Mode';
import ListTerms from './ListTerms';
import AddSetToFolder from './AddSetToFolder';

const SetPage = () => {
    const [openEditableTerm, setOpenEditableTerm] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [navigatePath, setNavigatePath] = useState('');
    const [sortByAlphabet, setSortByAlphabet] = useState(false);
    const [filteredTerms, setFilteredTerms] = useState([]);
    const { id, } = useParams();
    const { user, } = useAuth();
    const { enqueueSnackbar, } = useSnackbar();
    const navigate = useNavigate();
    const { document, isPending, error, } = useDocument({
        col: 'sets',
        id,
        uid: user.uid,
    });
    const { documents, } = useCollection({
        col: 'folders',
        _query: [['uid', '==', user.uid]],
    });
    const isFirstMount = useFirstMountState();

    const filterTerms = (terms) => {
        if (!sortByAlphabet) {
            return terms;
        }

        let filteredTerms = (terms).slice();

        if (sortByAlphabet) {
            filteredTerms = _.sortBy(filteredTerms, [(t) => t.eng]);
        }

        return filteredTerms;
    };

    const handleClickOperations = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseOperations = () => {
        setAnchorEl(null);
    };

    const handleClickOpenDialog = () => {
        handleCloseOperations();
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSetDelete = async (document) => {
        let errorBatch;
        const batch = writeBatch(db);

        document.foldersIds?.forEach((folderId) => {
            batch.update(doc(db, 'folders', folderId), {
                setsIds: documents.find((doc) => doc.id === folderId)?.setsIds?.filter((setId) => setId !== id),
            });
        });

        batch.delete(doc(db, 'sets', document.id));

        try {
            await batch.commit();
        } catch (error) {
            errorBatch = error;
            enqueueSnackbar(error.message, { variant: 'error', });
        }

        if (!errorBatch) {
            enqueueSnackbar(`Сет «${document.title}» удален`, { variant: 'success', });
            setNavigatePath('/sets');
        }
    };

    useEffect(() => {
        document && setFilteredTerms(filterTerms(document.terms));
    }, [document, sortByAlphabet]);

    useEffect(() => {
        navigatePath && navigate(navigatePath);
    }, [navigatePath]);

    return (
        isFirstMount
            ? null
            : isPending
            ? <SimpleBackdrop open={isPending} />
            : error
            ? <Alert severity='error' variant='filled'>{error.message}</Alert>
            : document
            ? (<Box>
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        sx={{mb: document.description ? undefined : [2, 2.5, 3, 3.5],}}
                    >
                        <Typography
                            component='h2'
                            variant='cropped'
                            sx={{
                                fontSize: [20, 22, 24, 27],
                                fontWeight: 700,
                                mr: [1, 2, 3, 4],}}>{document.title}</Typography>
                        <OperationsCustomizedMenu
                            title='set'
                            anchorEl={anchorEl}
                            handleClickOperations={handleClickOperations}
                            handleCloseOperations={handleCloseOperations}
                        >
                            <MenuItem
                                component={Link}
                                to={`/set/edit/${document.id}`}
                                onClick={handleCloseOperations}>
                                Редактировать
                            </MenuItem>
                            <MenuItem onClick={handleClickOpenDialog}>
                                Удалить
                            </MenuItem>
                        </OperationsCustomizedMenu>
                        <Dialog
                            open={openDialog}
                            aria-labelledby='alert-dialog-title'
                            aria-describedby='alert-dialog-description'>
                        <DialogTitle
                            id='alert-dialog-title'
                            sx={{
                                bgcolor: 'common.kashmirBlue',
                                color: 'common.pattensBlue',
                                display: 'block',
                                overflowWrap: 'anywhere',
                            }}>
                            {`Вы действительно хотите удалить сет «${document.title}»?`}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id='alert-dialog-description'>
                                Для продолжения работы с сайтом выберите одно из предложенных действий
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                alignItems: ['stretch', 'center'],
                                flexDirection: ['column', 'row'],
                                '& .MuiButton-root:last-child': {
                                    ml: [0, 1],
                                },
                            }}>
                            <Button
                                onClick={handleCloseDialog}
                                sx={{mb: [1, 0],}}>Отмена</Button>
                            <Button onClick={() => handleSetDelete(document)}>
                                Удалить
                            </Button>
                        </DialogActions>
                    </Dialog>
                    </Stack>

                     {document.description && (
                            <Typography sx={{
                                color: 'text.secondary',
                                fontSize: [11, 12, 13],
                                fontStyle: 'italic',
                                overflowWrap: 'anywhere',
                                mt: [0.25, 0.5, 0.75, 1],
                                mb: [2, 2.5, 3, 3.5],
                            }}>
                                {document.description}
                            </Typography>
                     )}

                    <ListFoldersCustomChips sx={{mb: [4, 5, 6, 7]}} />

                    <Mode document={document} filteredTerms={filteredTerms} sx={{mb: [4, 5, 6, 7]}} />

                    { (document.terms?.length) ? (
                        <>
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='center'
                                sx={{mb: 3,}}>
                                <Typography component='h4' variant='h5' sx={{fontSize: [16, 19, 22, 25]}}>Карточек в сете ({document.terms.length})</Typography>
                                <Tooltip title={(sortByAlphabet) ? 'Оригинал' : 'Сортировка по алфавиту'} placement='left' arrow>
                                    <IconButton
                                        disabled={openEditableTerm}
                                        onClick={() => setSortByAlphabet((prev) => !prev)}
                                        sx={{
                                            border: 1,
                                            borderColor: sortByAlphabet ? 'secondary.main' : 'borderColor.main',
                                            borderRadius: 0,
                                            p: 0,
                                            '&:hover': {
                                                borderColor: 'secondary.main',
                                                '& .MuiSvgIcon-root': {
                                                    color: 'secondary.main',
                                                },
                                            },
                                            '&:disabled': {
                                                opacity: 0.7,
                                            },
                                        }}>
                                        <AbcIcon sx={{
                                            color: sortByAlphabet && 'secondary.main',
                                            fontSize: 32,}} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <ListTerms
                                document={document}
                                filteredTerms={filteredTerms}
                                openEditableTerm={openEditableTerm}
                                setOpenEditableTerm={setOpenEditableTerm}
                                sx={{mb: [4, 5, 6, 7]}} />
                            <AddSetToFolder />
                        </>
                    ) : <Typography sx={{fontSize: 14,}}>Список терминов пуст</Typography>}
                </Box>)
            : <Typography sx={{fontSize: 14,}}>Запрашиваемые данные отсутствуют</Typography>
    );
};

export default SetPage;